// Человеческий ритм «печатает…» виджета. Живой собеседник сперва ЧИТАЕТ
// реплику, потом набирает ПАЧКАМИ — набор, пауза «подумать», снова набор — и
// только затем отправляет; непрерывный дотс на всю генерацию ответа выдаёт
// бота так же, как ответ «в ту же миллисекунду» (замечание владельца
// 01.08.2026). Чистый модуль без DOM — длительности тестируются юнит-тестами
// (pacing.test.mjs); случайность инъектируется параметрами roll/jitter,
// в проде виджет передаёт Math.random().
//
// Константы набора перед пузырём зеркалит сервер для мессенджеров
// (Assistants::Pacing::BUBBLE_*) — темп одинаков во всех каналах.

// Пауза «на прочтение» перед ПЕРВЫМ «печатает…» (порт marketpult
// d7f01e7+66e46ef): растёт с длиной прочитанного в клампе [MIN, MAX].
// Минимум поднят 400 → 1100 (01.08.2026): 0.3–0.5 с на короткой реплике
// читались как «начал печатать мгновенно» — человек столько не читает.
const READ_PAUSE_MIN_MS = 1100;
const READ_PAUSE_MAX_MS = 2500;
const READ_MS_PER_CHAR = 10;

// «Печатает…» перед следующим пузырём многопузырькового ответа: растёт с его
// длиной — короткая строка всё равно даёт такт, длинная не подвешивает ленту.
const TYPING_MIN_MS = 1200;
const TYPING_MAX_MS = 3500;
const TYPING_MS_PER_CHAR = 35;

// Цикл ожидания ответа: индикатор горит случайный «burst», гаснет на случайный
// «rest» и загорается снова — пока ответ не пришёл.
const TYPING_BURST_MIN_MS = 1400;
const TYPING_BURST_MAX_MS = 3800;
const TYPING_REST_MIN_MS = 600;
const TYPING_REST_MAX_MS = 1700;

// Короткий «добор»: ответ пришёл в паузе цикла (дотс погашен) — перед первым
// пузырём индикатор поднимается ещё раз, чтобы сообщение не выпадало из
// ниоткуда: человек ДОпечатывает и отправляет.
const TYPING_TAIL_MIN_MS = 700;
const TYPING_TAIL_MAX_MS = 1600;

// Пауза-осадка между пузырями одного ответа. Была фиксированной (700 мс) —
// одинаковый такт сам по себе выдавал робота.
const BUBBLE_GAP_MIN_MS = 450;
const BUBBLE_GAP_MAX_MS = 1200;

// Набор длиннее этого рвётся паузой посередине (typingHeadMs): даже один
// пузырь не печатается единой очередью.
const TYPING_SPLIT_MIN_MS = 2200;

/** Точка клампа [min, max] по броску roll ∈ [0, 1]. */
function spanMs(min: number, max: number, roll: number): number {
  return Math.round(min + (max - min) * roll);
}

/** Пауза «на прочтение» предыдущей реплики (мс) перед тем, как поднять
 *  «печатает…»: ~пропорциональна длине прочитанного, в клампе [MIN, MAX].
 *  jitter по умолчанию 1 — чистая детерминированная функция (тестируемо);
 *  в проде виджет даёт 0.7–1.3. Порт marketpult SiteChat#readPause. */
export function readDelayMs(prevText: string, jitter = 1): number {
  const read = Math.max(READ_PAUSE_MIN_MS, (prevText || '').length * READ_MS_PER_CHAR);
  return Math.min(READ_PAUSE_MAX_MS, read * jitter);
}

/** «Печатает…» перед пузырём: ~человеческая скорость набора в клампе;
 *  jitter (в проде 0.75–1.25) ломает одинаковый такт одинаковых длин. */
export function typingDelayMs(text: string, jitter = 1): number {
  const typed = Math.max(TYPING_MIN_MS, (text || '').length * TYPING_MS_PER_CHAR);
  return Math.min(TYPING_MAX_MS, typed * jitter);
}

/** Сколько горит «печатает…» в цикле ожидания ответа. */
export function burstMs(roll: number): number {
  return spanMs(TYPING_BURST_MIN_MS, TYPING_BURST_MAX_MS, roll);
}

/** Сколько дотс погашен между пачками набора («думает»). */
export function restMs(roll: number): number {
  return spanMs(TYPING_REST_MIN_MS, TYPING_REST_MAX_MS, roll);
}

/** Короткий добор перед пузырём, пришедшим при погашенном дотсе. */
export function tailMs(roll: number): number {
  return spanMs(TYPING_TAIL_MIN_MS, TYPING_TAIL_MAX_MS, roll);
}

/** Пауза-осадка между пузырями одного ответа. */
export function bubbleGapMs(roll: number): number {
  return spanMs(BUBBLE_GAP_MIN_MS, BUBBLE_GAP_MAX_MS, roll);
}

/** Первая пачка длинного набора: 35–65% общей длительности — дальше пауза
 *  «подумать» и добор остатка. Короткий набор не рвём — 0. */
export function typingHeadMs(total: number, roll: number): number {
  if (total < TYPING_SPLIT_MIN_MS) return 0;
  return Math.round(total * (0.35 + roll * 0.3));
}
